import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Publish as PublishIcon,
  ExpandMore as ExpandMoreIcon,
  DeleteOutline as DeleteOutlineIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  UploadFile as UploadFileIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const AdminPerformanceManagement = () => {
  const { getAuthHeaders } = useAuth();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [dialogMode, setDialogMode] = useState('create');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reviewCycle: 'Q1',
    reviewYear: new Date().getFullYear(),
    startDate: '',
    endDate: '',
    alpha: 0.3,
    beta: 0.7,
    kras: [],
    mapping: {
      type: 'all',
      departments: [],
      roles: [],
      employees: []
    }
  });
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchForms();
    fetchDepartments();
    fetchUsers();
  }, []);

  const fetchForms = async () => {
    try {
      const response = await axios.get(`${API_URL}/performance/forms`, {
        headers: getAuthHeaders()
      });
      setForms(response.data.forms);
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast.error('Failed to fetch performance forms');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API_URL}/departments`, {
        headers: getAuthHeaders()
      });
      // API returns array directly, not wrapped in departments property
      const deptList = Array.isArray(response.data) ? response.data : (response.data.departments || []);
      setDepartments(deptList);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: getAuthHeaders()
      });
      // API returns array directly, not wrapped in users property
      const userList = Array.isArray(response.data) ? response.data : (response.data.users || []);
      setUsers(userList);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateForm = () => {
    setDialogMode('create');
    setSelectedForm(null);
    setFormData({
      title: '',
      description: '',
      reviewCycle: 'Q1',
      reviewYear: new Date().getFullYear(),
      startDate: '',
      endDate: '',
      alpha: 0.3,
      beta: 0.7,
      kras: [],
      mapping: {
        type: 'all',
        departments: [],
        roles: [],
        employees: []
      }
    });
    setOpenDialog(true);
  };

  const handleEditForm = (form) => {
    setDialogMode('edit');
    setSelectedForm(form);
    setFormData({
      title: form.title,
      description: form.description || '',
      reviewCycle: form.reviewCycle,
      reviewYear: form.reviewYear,
      startDate: form.startDate ? new Date(form.startDate).toISOString().split('T')[0] : '',
      endDate: form.endDate ? new Date(form.endDate).toISOString().split('T')[0] : '',
      alpha: form.alpha,
      beta: form.beta,
      kras: form.kras || [],
      mapping: form.mapping || { type: 'all', departments: [], roles: [], employees: [] }
    });
    setOpenDialog(true);
  };

  const handleAddKRA = () => {
    setFormData({
      ...formData,
      kras: [...formData.kras, {
        name: '',
        description: '',
        weightage: 0,
        kpis: []
      }]
    });
  };

  const handleUpdateKRA = (index, field, value) => {
    const updatedKras = [...formData.kras];
    updatedKras[index][field] = value;
    setFormData({ ...formData, kras: updatedKras });
  };

  const handleDeleteKRA = (index) => {
    const updatedKras = formData.kras.filter((_, i) => i !== index);
    setFormData({ ...formData, kras: updatedKras });
  };

  const handleAddKPI = (kraIndex) => {
    const updatedKras = [...formData.kras];
    updatedKras[kraIndex].kpis.push({
      name: '',
      description: ''
    });
    setFormData({ ...formData, kras: updatedKras });
  };

  const handleUpdateKPI = (kraIndex, kpiIndex, field, value) => {
    const updatedKras = [...formData.kras];
    updatedKras[kraIndex].kpis[kpiIndex][field] = value;
    setFormData({ ...formData, kras: updatedKras });
  };

  const handleDeleteKPI = (kraIndex, kpiIndex) => {
    const updatedKras = [...formData.kras];
    updatedKras[kraIndex].kpis = updatedKras[kraIndex].kpis.filter((_, i) => i !== kpiIndex);
    setFormData({ ...formData, kras: updatedKras });
  };

  const calculateTotalWeightage = () => {
    return formData.kras.reduce((sum, kra) => sum + (parseFloat(kra.weightage) || 0), 0);
  };

  const handleImportExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls'].includes(fileExtension)) {
      toast.error('Please upload a valid Excel file (.xlsx or .xls)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
        
        // Parse the Excel data
        // Expected format:
        // Row 1: Headers (Type, Name, Description, Weightage)
        // Row 2+: Data rows
        // KRA rows: Type="KRA", Name, Description, Weightage
        // KPI rows: Type="KPI", Name, Description, Weightage (empty or "-")
        
        if (jsonData.length < 2) {
          toast.error('Excel file must have at least a header row and one data row');
          return;
        }

        const headers = jsonData[0].map(h => String(h).toLowerCase().trim());
        
        // Find column indices - flexible column detection
        const typeIndex = headers.findIndex(h => h.includes('type'));
        const kraIndex = headers.findIndex(h => h.includes('kra') || h.includes('kka'));
        const kpiIndex = headers.findIndex(h => h.includes('kpi') || h.includes('task'));
        const nameIndex = headers.findIndex(h => (h.includes('name') && !h.includes('kra') && !h.includes('kpi')) || (kpiIndex === -1 && (h.includes('kpi') || h.includes('task'))));
        const descIndex = headers.findIndex(h => h.includes('description') || h.includes('desc'));
        const weightIndex = headers.findIndex(h => h.includes('weightage') || h.includes('weight'));
        const srNoIndex = headers.findIndex(h => h.includes('sr') || h.includes('serial') || h.includes('s.no'));

        // Determine which columns to use
        // If KRA and KPI columns exist, use them; otherwise use Type and Name
        const useKRAKPIFormat = kraIndex !== -1 && (kpiIndex !== -1 || nameIndex !== -1);
        const useTypeFormat = typeIndex !== -1 && nameIndex !== -1;
        
        if (!useKRAKPIFormat && !useTypeFormat) {
          toast.error('Excel file must have either "KRA" and "KPI/Task" columns OR "Type" and "Name" columns');
          return;
        }

        const importedKRAs = [];
        let currentKRA = null;
        let foundFirstSection = false; // Track if we've found the first section marker
        const tasksBeforeFirstKRA = []; // Collect tasks before first section marker

        // Process data rows (skip header row)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          let kraName = '';
          let kpiName = '';
          let description = '';
          let weightage = 0;
          let type = '';
          let sectionMarker = '';

          // Check for section markers (A, B, C, etc.) - prioritize Sr No column, then check all columns
          // These are the ONLY indicators of new main KRAs
          if (srNoIndex !== -1) {
            const srNoValue = String(row[srNoIndex] || '').trim().toUpperCase();
            // Check if Sr No column has a single letter (A-Z) - this is a section marker
            if (srNoValue.length === 1 && /^[A-Z]$/.test(srNoValue)) {
              sectionMarker = srNoValue;
            }
          }
          
          // If not found in Sr No column, check all columns
          if (!sectionMarker) {
            for (let col = 0; col < row.length; col++) {
              const cellValue = String(row[col] || '').trim().toUpperCase();
              // Check if it's a single letter (A-Z) which is a section marker
              if (cellValue.length === 1 && /^[A-Z]$/.test(cellValue)) {
                sectionMarker = cellValue;
                break;
              }
            }
          }

          if (useKRAKPIFormat) {
            // Format: KRA column, KPI/Task column, Weightage column
            kraName = kraIndex !== -1 ? String(row[kraIndex] || '').trim() : '';
            const taskColIndex = kpiIndex !== -1 ? kpiIndex : nameIndex;
            kpiName = taskColIndex !== -1 ? String(row[taskColIndex] || '').trim() : '';
            description = descIndex !== -1 ? String(row[descIndex] || '').trim() : '';
            // Parse weightage - handle both percentage format (20%) and decimal format (0.2 or 20)
            if (weightIndex !== -1) {
              const weightValue = String(row[weightIndex] || '').trim();
              if (weightValue) {
                // Check if it's a percentage format (contains %)
                if (weightValue.includes('%')) {
                  // Remove % and parse as number (20% -> 20)
                  weightage = parseFloat(weightValue.replace('%', '')) || 0;
                } else {
                  // Check if it's a decimal (0.2, 0.3) or whole number (20, 30)
                  const parsed = parseFloat(weightValue) || 0;
                  // If value is less than 1, assume it's a decimal and convert to percentage (0.2 -> 20)
                  // If value is 1 or greater, assume it's already a percentage (20 -> 20)
                  weightage = parsed < 1 ? parsed * 100 : parsed;
                }
              } else {
                weightage = 0;
              }
            } else {
              weightage = 0;
            }

            // Collect tasks before first section marker
            if (!foundFirstSection && !sectionMarker) {
              // This is a task before the first KRA - collect it
              if (kpiName) {
                tasksBeforeFirstKRA.push({
                  name: kpiName,
                  description: kraName || description
                });
              } else if (kraName) {
                tasksBeforeFirstKRA.push({
                  name: kraName,
                  description: description
                });
              }
              continue; // Continue to next row
            }
            
            if (sectionMarker) {
              foundFirstSection = true; // Mark that we've found the first section
            }
            
            // ONLY section markers (A, B, C) create new main KRAs
            // Everything between section markers belongs to the current main KRA
            if (sectionMarker) {
              // Section marker found - this is a NEW main KRA
              if (currentKRA) {
                importedKRAs.push(currentKRA);
              }
              // Get the main KRA name from the KRA column in the section marker row
              // The KRA column should contain the main KRA name for this section
              // If KRA column is empty in section row, look for it in KPI/Task or use section marker
              let mainKRAName = kraName;
              if (!mainKRAName && kpiName) {
                // Sometimes the main KRA name might be in KPI/Task column in section row
                mainKRAName = kpiName;
              }
              if (!mainKRAName) {
                mainKRAName = `Section ${sectionMarker}`;
              }
              
              currentKRA = {
                name: mainKRAName,
                description: description,
                weightage: weightage,
                kpis: []
              };
              
              // If this is the first KRA, add tasks collected before first section marker
              if (importedKRAs.length === 0 && tasksBeforeFirstKRA.length > 0) {
                currentKRA.kpis = [...tasksBeforeFirstKRA];
              }
              
              // IMPORTANT: When there's a task column value in the same row as a section marker (A, B, C),
              // it is NOT an individual KPI - it's just descriptive context for the KRA itself.
              // We should NOT add it as a KPI for ANY KRA (not just the first one).
              // The task column in the KRA row is just context, not a separate KPI.
              // Do nothing with kpiName here - it's not a KPI, just context
            } else if (currentKRA && foundFirstSection) {
              // No section marker but we have a current KRA - this row belongs to the current main KRA
              // This includes rows with numbers (1, 2, 3) in Sr No column - these are sub-items/KPIs
              // KRA column values here are sub-KRAs or additional context - they are NOT new main KRAs
              
              // Check if there's any content in the row - look in all columns if KRA and KPI columns are empty
              let contentFromOtherColumn = '';
              
              // If both KRA and KPI columns are empty, check ALL other columns for content
              // This handles cases where sub-items might be in different column positions
              // Also handles cases where KKA column is empty but content is in another column
              if (!kraName && !kpiName) {
                // First, try to find content in any column (except Sr No and Weightage)
                for (let col = 0; col < row.length; col++) {
                  // Skip Sr No and Weightage columns (these are not content)
                  if (col === srNoIndex || col === weightIndex) continue;
                  const cellValue = String(row[col] || '').trim();
                  // Check if it has meaningful content (not just numbers or empty)
                  if (cellValue && cellValue.length > 0) {
                    // If it's a single letter (A-Z), it's a section marker, skip it
                    if (cellValue.length === 1 && /^[A-Z]$/i.test(cellValue)) continue;
                    // If it's just a number, skip it (might be Sr No in wrong column)
                    if (/^\d+$/.test(cellValue)) continue;
                    // Found content!
                    contentFromOtherColumn = cellValue;
                    break;
                  }
                }
              }
              
              // Process the content - prioritize KPI/Task column, then KRA column, then other columns
              // Always process if we have ANY content (kpiName, kraName, or contentFromOtherColumn)
              if (kpiName) {
                // KPI/Task column has value - this is the KPI metric name
                // KRA column in sub-rows holds the task objective — store it as description
                currentKRA.kpis.push({
                  name: kpiName,
                  description: kraName || description
                });
              } else if (kraName) {
                // Only KRA column has value (sub-KRA) - add as KPI
                // This handles cases where there are sub-KRAs within a main KRA
                // Examples: "Internal Team Coordination", "Expense Reimbursement Checking", "Team Head to input"
                currentKRA.kpis.push({
                  name: kraName,
                  description: description
                });
              } else if (contentFromOtherColumn) {
                // Content found in other column (not KRA or KPI column) - add as KPI
                // This handles cases where sub-items are in different column positions
                // Also handles cases where KKA column is empty but content exists elsewhere
                currentKRA.kpis.push({
                  name: contentFromOtherColumn,
                  description: description
                });
              }
              // Note: If no content found at all (no kpiName, no kraName, no contentFromOtherColumn),
              // the row is effectively skipped (no KPI added)
            }
            // If no section marker and no current KRA, skip the row (shouldn't happen after first section)
          } else if (useTypeFormat) {
            // Original format: Type column, Name column
            type = String(row[typeIndex] || '').trim().toUpperCase();
            const name = String(row[nameIndex] || '').trim();
            description = descIndex !== -1 ? String(row[descIndex] || '').trim() : '';
            // Parse weightage - handle both percentage format (20%) and decimal format (0.2 or 20)
            if (weightIndex !== -1) {
              const weightValue = String(row[weightIndex] || '').trim();
              if (weightValue) {
                // Check if it's a percentage format (contains %)
                if (weightValue.includes('%')) {
                  // Remove % and parse as number (20% -> 20)
                  weightage = parseFloat(weightValue.replace('%', '')) || 0;
                } else {
                  // Check if it's a decimal (0.2, 0.3) or whole number (20, 30)
                  const parsed = parseFloat(weightValue) || 0;
                  // If value is less than 1, assume it's a decimal and convert to percentage (0.2 -> 20)
                  // If value is 1 or greater, assume it's already a percentage (20 -> 20)
                  weightage = parsed < 1 ? parsed * 100 : parsed;
                }
              } else {
                weightage = 0;
              }
            } else {
              weightage = 0;
            }

            if (!name && !sectionMarker) continue; // Skip empty rows

            // Section marker takes precedence
            if (sectionMarker) {
              if (currentKRA) {
                importedKRAs.push(currentKRA);
              }
              currentKRA = {
                name: name || `Section ${sectionMarker}`,
                description: description,
                weightage: weightage,
                kpis: []
              };
            } else if (type === 'KRA') {
              // Save previous KRA if exists
              if (currentKRA) {
                importedKRAs.push(currentKRA);
              }
              // Create new KRA
              currentKRA = {
                name: name,
                description: description,
                weightage: weightage,
                kpis: []
              };
            } else if (type === 'KPI' && currentKRA) {
              // Add KPI to current KRA
              currentKRA.kpis.push({
                name: name,
                description: description
              });
            } else if (!type || type === '') {
              // If no type specified, assume it's a KPI if we have a KRA, otherwise it's a KRA
              if (currentKRA) {
                currentKRA.kpis.push({
                  name: name,
                  description: description
                });
              } else {
                // Create a new KRA with default weightage
                if (currentKRA) {
                  importedKRAs.push(currentKRA);
                }
                currentKRA = {
                  name: name,
                  description: description,
                  weightage: weightage || 0,
                  kpis: []
                };
              }
            }
          }
        }

        // Add the last KRA
        if (currentKRA) {
          importedKRAs.push(currentKRA);
        }

        if (importedKRAs.length === 0) {
          toast.error('No valid KRAs found in the Excel file');
          return;
        }

        // Update form data with imported KRAs
        setFormData({
          ...formData,
          kras: importedKRAs
        });

        toast.success(`Successfully imported ${importedKRAs.length} KRA(s) with ${importedKRAs.reduce((sum, kra) => sum + kra.kpis.length, 0)} KPI(s)`);
        
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        toast.error('Failed to parse Excel file. Please check the format.');
      }
    };

    reader.onerror = () => {
      toast.error('Error reading file');
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSaveForm = async () => {
    try {
      // Validate total weightage
      const totalWeightage = calculateTotalWeightage();
      if (Math.abs(totalWeightage - 100) > 0.01) {
        toast.error(`Total KRA weightage must equal 100%. Current: ${totalWeightage.toFixed(2)}%`);
        return;
      }

      // Validate alpha + beta
      if (Math.abs((formData.alpha + formData.beta) - 1) > 0.01) {
        toast.error('Employee rating weight + Manager rating weight must equal 1');
        return;
      }

      // Validate KRAs and KPIs
      for (let i = 0; i < formData.kras.length; i++) {
        const kra = formData.kras[i];
        if (!kra.name || !kra.weightage) {
          toast.error(`KRA ${i + 1} must have a name and weightage`);
          return;
        }
        if (kra.kpis.length === 0) {
          toast.error(`KRA ${i + 1} must have at least one KPI`);
          return;
        }
        for (let j = 0; j < kra.kpis.length; j++) {
          if (!kra.kpis[j].name) {
            toast.error(`KPI ${j + 1} in KRA ${i + 1} must have a name`);
            return;
          }
        }
      }

      const url = dialogMode === 'create'
        ? `${API_URL}/performance/forms`
        : `${API_URL}/performance/forms/${selectedForm._id}`;
      
      const method = dialogMode === 'create' ? 'post' : 'put';

      await axios[method](url, formData, {
        headers: getAuthHeaders()
      });

      toast.success(`Performance form ${dialogMode === 'create' ? 'created' : 'updated'} successfully`);
      setOpenDialog(false);
      fetchForms();
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error(error.response?.data?.message || 'Failed to save performance form');
    }
  };

  const handlePublishForm = async (formId) => {
    if (!window.confirm('Are you sure you want to publish this form? This will create reviews for all eligible employees.')) {
      return;
    }

    try {
      await axios.post(`${API_URL}/performance/forms/${formId}/publish`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Performance form published successfully');
      fetchForms();
    } catch (error) {
      console.error('Error publishing form:', error);
      toast.error(error.response?.data?.message || 'Failed to publish performance form');
    }
  };

  const handleDeleteForm = async (formId) => {
    if (!window.confirm('Are you sure you want to delete this form? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/performance/forms/${formId}`, {
        headers: getAuthHeaders()
      });
      toast.success('Performance form deleted successfully');
      fetchForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      toast.error(error.response?.data?.message || 'Failed to delete performance form');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Performance Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateForm}
        >
          Create Form
        </Button>
      </Box>

      <Grid container spacing={3}>
        {forms.map((form) => (
          <Grid item xs={12} md={6} lg={4} key={form._id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                  <Typography variant="h6">{form.title}</Typography>
                  <Chip
                    label={form.status}
                    color={
                      form.status === 'published' ? 'success' :
                      form.status === 'draft' ? 'default' : 'error'
                    }
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {form.reviewCycle} {form.reviewYear}
                </Typography>
                {form.description && (
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {form.description}
                  </Typography>
                )}
                <Typography variant="body2" gutterBottom>
                  <strong>KRAs:</strong> {form.kras?.length || 0}
                </Typography>
                <Typography variant="body2" gutterBottom>
                  <strong>Employee Rating:</strong> {form.alpha} | <strong>Manager Rating:</strong> {form.beta}
                </Typography>
                <Box mt={2} display="flex" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleEditForm(form)}
                    disabled={form.status === 'published'}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  {form.status === 'draft' && (
                    <IconButton
                      size="small"
                      onClick={() => handlePublishForm(form._id)}
                    >
                      <PublishIcon fontSize="small" />
                    </IconButton>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteForm(form._id)}
                    disabled={form.status === 'published'}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Form Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create Performance Form' : 'Edit Performance Form'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Review Cycle</InputLabel>
                  <Select
                    value={formData.reviewCycle}
                    onChange={(e) => setFormData({ ...formData, reviewCycle: e.target.value })}
                    label="Review Cycle"
                  >
                    <MenuItem value="Q1">Q1</MenuItem>
                    <MenuItem value="Q2">Q2</MenuItem>
                    <MenuItem value="Q3">Q3</MenuItem>
                    <MenuItem value="Q4">Q4</MenuItem>
                    <MenuItem value="Annual">Annual</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Review Year"
                  type="number"
                  value={formData.reviewYear}
                  onChange={(e) => setFormData({ ...formData, reviewYear: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Employee Rating Weight"
                  type="number"
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  value={formData.alpha}
                  onChange={(e) => {
                    const alpha = parseFloat(e.target.value);
                    setFormData({ ...formData, alpha, beta: 1 - alpha });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Manager Rating Weight"
                  type="number"
                  inputProps={{ min: 0, max: 1, step: 0.1 }}
                  value={formData.beta}
                  onChange={(e) => {
                    const beta = parseFloat(e.target.value);
                    setFormData({ ...formData, beta, alpha: 1 - beta });
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Key Result Areas (KRAs) & KPIs</Typography>
                  <Box display="flex" gap={1}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={handleImportExcel}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<UploadFileIcon />}
                      onClick={() => fileInputRef.current?.click()}
                      color="secondary"
                    >
                      Import Excel
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddKRA}
                    >
                      Add KRA
                    </Button>
                  </Box>
                </Box>
                {calculateTotalWeightage() !== 100 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Total weightage: {calculateTotalWeightage().toFixed(2)}% (Must be 100%)
                  </Alert>
                )}
                
                {/* Excel-like Table Interface - Matching Excel Format */}
                <TableContainer component={Paper} sx={{ maxHeight: '500px', overflow: 'auto', border: '1px solid #e0e0e0', width: '100%' }}>
                  <Table stickyHeader size="small" sx={{ '& .MuiTableCell-root': { borderRight: '1px solid #e0e0e0', borderBottom: '1px solid #e0e0e0', padding: '8px' }, width: '100%' }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ minWidth: 80, bgcolor: 'grey.200', fontWeight: 'bold', borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>Sr No</TableCell>
                        <TableCell sx={{ minWidth: 350, bgcolor: 'grey.200', fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>KRA</TableCell>
                        <TableCell sx={{ minWidth: 500, bgcolor: 'grey.200', fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>KPI / Task</TableCell>
                        <TableCell sx={{ minWidth: 150, bgcolor: 'grey.200', fontWeight: 'bold', borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>Weightage</TableCell>
                        <TableCell sx={{ minWidth: 120, bgcolor: 'grey.200', fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        let srNo = 0;
                        let kpiSrNo = 0;
                        return formData.kras.map((kra, kraIndex) => {
                          kpiSrNo = 0; // Reset KPI serial number for each KRA
                          return (
                            <React.Fragment key={`kra-${kraIndex}`}>
                              {/* KRA Row */}
                              <TableRow sx={{ bgcolor: 'primary.50', '&:hover': { bgcolor: 'primary.100' } }}>
                                <TableCell sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center', fontWeight: 'bold' }}>
                                  {String.fromCharCode(65 + kraIndex)} {/* A, B, C, etc. */}
                                </TableCell>
                                <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                  <TextField
                                    fullWidth
                                    variant="standard"
                                    value={kra.name || ''}
                                    onChange={(e) => handleUpdateKRA(kraIndex, 'name', e.target.value)}
                                    placeholder="Enter KRA name"
                                    size="small"
                                    required
                                    InputProps={{ disableUnderline: false }}
                                  />
                                </TableCell>
                                <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                  <TextField
                                    fullWidth
                                    variant="standard"
                                    value={kra.description || ''}
                                    onChange={(e) => handleUpdateKRA(kraIndex, 'description', e.target.value)}
                                    placeholder="KRA description (optional)"
                                    size="small"
                                    multiline
                                    maxRows={2}
                                    InputProps={{ disableUnderline: false }}
                                  />
                                </TableCell>
                                <TableCell sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                                  <TextField
                                    fullWidth
                                    variant="standard"
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 0.1, style: { textAlign: 'center' } }}
                                    value={kra.weightage || ''}
                                    onChange={(e) => handleUpdateKRA(kraIndex, 'weightage', parseFloat(e.target.value) || 0)}
                                    placeholder="0"
                                    size="small"
                                    required
                                    InputProps={{ disableUnderline: false }}
                                    sx={{ '& input': { textAlign: 'center' } }}
                                  />
                                </TableCell>
                                <TableCell sx={{ textAlign: 'center' }}>
                                  <Box display="flex" gap={0.5} justifyContent="center">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleAddKPI(kraIndex)}
                                      color="primary"
                                      title="Add KPI"
                                    >
                                      <AddCircleOutlineIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteKRA(kraIndex)}
                                      color="error"
                                      title="Delete KRA"
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              {/* KPI Rows */}
                              {kra.kpis.map((kpi, kpiIndex) => {
                                kpiSrNo++;
                                return (
                                  <TableRow key={`kpi-${kraIndex}-${kpiIndex}`} sx={{ bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                                      {kpiSrNo}
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                      <TextField
                                        fullWidth
                                        variant="standard"
                                        value={kpi.description || ''}
                                        onChange={(e) => handleUpdateKPI(kraIndex, kpiIndex, 'description', e.target.value)}
                                        placeholder="Enter task / objective"
                                        size="small"
                                        InputProps={{ disableUnderline: false }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0' }}>
                                      <TextField
                                        fullWidth
                                        variant="standard"
                                        value={kpi.name || ''}
                                        onChange={(e) => handleUpdateKPI(kraIndex, kpiIndex, 'name', e.target.value)}
                                        placeholder="Enter KPI metric"
                                        size="small"
                                        required
                                        InputProps={{ disableUnderline: false }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ borderRight: '1px solid #e0e0e0', textAlign: 'center' }}>
                                      {/* Blank for KPI rows */}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleDeleteKPI(kraIndex, kpiIndex)}
                                        color="error"
                                        title="Delete KPI"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                              {kra.kpis.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={5} align="center" sx={{ py: 2, borderRight: '1px solid #e0e0e0' }}>
                                    <Button
                                      size="small"
                                      startIcon={<AddIcon />}
                                      onClick={() => handleAddKPI(kraIndex)}
                                      variant="outlined"
                                    >
                                      Add First KPI
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        });
                      })()}
                      {formData.kras.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, borderRight: '1px solid #e0e0e0' }}>
                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              No KRAs added yet. Click "Add KRA" button above to start.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Form Mapping</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Mapping Type</InputLabel>
                  <Select
                    value={formData.mapping.type}
                    onChange={(e) => setFormData({
                      ...formData,
                      mapping: { ...formData.mapping, type: e.target.value }
                    })}
                    label="Mapping Type"
                  >
                    <MenuItem value="all">All Employees</MenuItem>
                    <MenuItem value="department">By Department</MenuItem>
                    <MenuItem value="role">By Role</MenuItem>
                    <MenuItem value="employee">Specific Employees</MenuItem>
                  </Select>
                </FormControl>
                {formData.mapping.type === 'department' && (
                  <FormControl fullWidth>
                    <InputLabel>Departments</InputLabel>
                    <Select
                      multiple
                      value={formData.mapping.departments || []}
                      onChange={(e) => setFormData({
                        ...formData,
                        mapping: { ...formData.mapping, departments: e.target.value }
                      })}
                      label="Departments"
                      renderValue={(selected) => {
                        if (!selected || selected.length === 0) return '';
                        return selected.join(', ');
                      }}
                    >
                      {departments.length > 0 ? (
                        departments.map((dept) => {
                          const deptName = dept.departmentName || dept.name;
                          return (
                            <MenuItem key={dept._id || dept.id} value={deptName}>
                              {deptName}
                            </MenuItem>
                          );
                        })
                      ) : (
                        <MenuItem disabled>No departments available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                )}
                {formData.mapping.type === 'role' && (
                  <FormControl fullWidth>
                    <InputLabel>Roles</InputLabel>
                    <Select
                      multiple
                      value={formData.mapping.roles}
                      onChange={(e) => setFormData({
                        ...formData,
                        mapping: { ...formData.mapping, roles: e.target.value }
                      })}
                      label="Roles"
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="itsupport">IT Support</MenuItem>
                    </Select>
                  </FormControl>
                )}
                {formData.mapping.type === 'employee' && (
                  <Autocomplete
                    fullWidth
                    multiple
                    options={users}
                    value={users.filter(user => (formData.mapping.employees || []).includes(user._id || user.id))}
                    onChange={(event, newValue) => {
                      setFormData({
                        ...formData,
                        mapping: { 
                          ...formData.mapping, 
                          employees: newValue.map(u => u._id || u.id)
                        }
                      });
                    }}
                    getOptionLabel={(option) => {
                      const name = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                      return name ? `${name} (${option.email || ''})` : (option.email || 'Unknown');
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employees"
                        placeholder="Search employees by name or email..."
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => {
                        const name = `${option.firstName || ''} ${option.lastName || ''}`.trim();
                        const label = name || option.email || 'Unknown';
                        return (
                          <Chip
                            {...getTagProps({ index })}
                            key={option._id || option.id}
                            label={label}
                          />
                        );
                      })
                    }
                    filterOptions={(options, { inputValue }) => {
                      return options.filter((option) => {
                        const searchTerm = inputValue.toLowerCase();
                        const firstName = (option.firstName || '').toLowerCase();
                        const lastName = (option.lastName || '').toLowerCase();
                        const email = (option.email || '').toLowerCase();
                        const fullName = `${firstName} ${lastName}`.trim();
                        return fullName.includes(searchTerm) || email.includes(searchTerm);
                      });
                    }}
                    noOptionsText="No employees found"
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveForm} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPerformanceManagement;

